import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Header from '../components/Header';
import { ArrowUp, ArrowDown, Trash2, Plus, Edit3, Music, Video, Upload, X, BookOpen, Images } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { saveMedia, deleteMedia } from '../utils/storage';
import { toast } from 'sonner';
import MediaGallery from '../components/MediaGallery';

const MAX_MEDIA_SIZE = 50 * 1024 * 1024; // 50MB

function newId(prefix = 'm') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export default function EditTopic() {
  const { topicId } = useParams();
  const { topics, reorderMantras, addMantra, updateMantra, deleteMantra } = useApp();
  const topic = topics.find(t => t.id === topicId);

  const [editing, setEditing] = useState(null); // mantra object being edited (or new)
  const [showDialog, setShowDialog] = useState(false);

  if (!topic) {
    return (
      <div className="min-h-screen">
        <Header title="नहीं मिला" showBack showSettings={false} />
        <div className="p-6">विषय नहीं मिला।</div>
      </div>
    );
  }

  const openAdd = () => {
    setEditing({
      id: newId('m'),
      name: '',
      text: '',
      meaning: '',
      audioUrl: '',
      videoUrl: '',
      meaningAudioUrl: '',
      pauseAfter: false,
      _isNew: true,
    });
    setShowDialog(true);
  };

  const openEdit = (m) => {
    setEditing({ ...m, _isNew: false });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!editing.name.trim() || !editing.text.trim()) {
      toast.error('कृपया मन्त्र का नाम और पाठ भरें।');
      return;
    }
    const { _isNew, ...m } = editing;
    if (_isNew) {
      addMantra(topic.id, m);
      toast.success('मन्त्र जोड़ा गया।');
    } else {
      updateMantra(topic.id, m.id, m);
      toast.success('मन्त्र अद्यतन हुआ।');
    }
    setShowDialog(false);
    setEditing(null);
  };

  const handleMove = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= topic.mantras.length) return;
    reorderMantras(topic.id, idx, newIdx);
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`"${m.name}" को हटाएं?`)) return;
    if (m.audioUrl) await deleteMedia(m.audioUrl);
    if (m.videoUrl) await deleteMedia(m.videoUrl);
    if (m.meaningAudioUrl) await deleteMedia(m.meaningAudioUrl);
    deleteMantra(topic.id, m.id);
    toast.success('मन्त्र हटाया गया।');
  };

  return (
    <div className="min-h-screen pb-24">
      <Header title={`${topic.title} (संपादन)`} subtitle={`${topic.mantras.length} मन्त्र`} showBack showSettings={false} />

      <div className="max-w-3xl mx-auto px-4 py-5">
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 text-xs text-orange-900 devanagari-ui">
          ऊपर/नीचे तीर से क्रम बदलें · संपादन के लिए मन्त्र पर क्लिक करें · नया मन्त्र जोड़ने के लिए नीचे बटन
        </div>

        <ul className="space-y-2">
          {topic.mantras.map((m, idx) => (
            <li key={m.id} className="bg-white rounded-xl border border-orange-100 p-3 flex items-start gap-2">
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => handleMove(idx, -1)}
                  disabled={idx === 0}
                  className="p-1 rounded hover:bg-orange-100 disabled:opacity-30 text-orange-700"
                  aria-label="Move up"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleMove(idx, 1)}
                  disabled={idx === topic.mantras.length - 1}
                  className="p-1 rounded hover:bg-orange-100 disabled:opacity-30 text-orange-700"
                  aria-label="Move down"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
              <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-800 devanagari-ui truncate">{m.name}</p>
                <p className="text-xs text-stone-500 devanagari truncate">{m.text}</p>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {m.audioUrl && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded flex items-center gap-1"><Music className="w-2.5 h-2.5" />Audio</span>}
                  {m.videoUrl && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex items-center gap-1"><Video className="w-2.5 h-2.5" />Video</span>}
                  {m.meaning && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded flex items-center gap-1"><BookOpen className="w-2.5 h-2.5" />अर्थ</span>}
                  {m.pauseAfter && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">विराम</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => openEdit(m)} className="p-1.5 rounded hover:bg-orange-100 text-orange-700" aria-label="Edit">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(m)} className="p-1.5 rounded hover:bg-red-100 text-red-600" aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Floating add button */}
      <button
        onClick={openAdd}
        className="fixed bottom-6 right-6 saffron-gradient text-white rounded-full shadow-lg w-14 h-14 flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Add"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Edit/Add dialog */}
      <MantraEditDialog
        open={showDialog}
        onClose={() => { setShowDialog(false); setEditing(null); }}
        editing={editing}
        setEditing={setEditing}
        onSave={handleSave}
      />
    </div>
  );
}

function MantraEditDialog({ open, onClose, editing, setEditing, onSave }) {
  const audioInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const meaningAudioInputRef = useRef(null);
  const [galleryFor, setGalleryFor] = useState(null); // 'audioUrl' | 'videoUrl' | 'meaningAudioUrl'

  if (!editing) return null;

  const handleFileUpload = async (file, field) => {
    if (!file) return;
    if (file.size > MAX_MEDIA_SIZE) {
      toast.error(`फ़ाइल बहुत बड़ी है (${(file.size / 1024 / 1024).toFixed(1)}MB)। अधिकतम 50MB।`);
      return;
    }
    const key = newId('media');
    try {
      // Don't delete old media key here — it might be referenced by other mantras (gallery sharing)
      await saveMedia(key, file);
      setEditing(prev => ({ ...prev, [field]: key }));
      toast.success('फ़ाइल अपलोड हुई।');
    } catch (e) {
      console.error(e);
      toast.error('अपलोड विफल।');
    }
  };

  const clearMedia = async (field) => {
    // Just unset the reference; don't delete the file (might be used elsewhere)
    setEditing(prev => ({ ...prev, [field]: '' }));
  };

  const handleGalleryPick = ({ mediaKey, text, meaning }) => {
    // Copy media key + text + meaning (only fill if user hasn't already typed)
    setEditing(prev => ({
      ...prev,
      [galleryFor]: mediaKey,
      text: prev.text && prev.text.trim() ? prev.text : text,
      meaning: prev.meaning && prev.meaning.trim() ? prev.meaning : meaning,
    }));
    toast.success('गैलरी से जोड़ा गया।');
    setGalleryFor(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="devanagari-ui">{editing._isNew ? 'नया मन्त्र जोड़ें' : 'मन्त्र संपादन'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="devanagari-ui text-xs">मन्त्र का नाम *</Label>
            <Input
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder="जैसे: गायत्री मन्त्र"
              className="devanagari-ui mt-1"
            />
          </div>

          <div>
            <Label className="devanagari-ui text-xs">मन्त्र पाठ *</Label>
            <Textarea
              value={editing.text}
              onChange={(e) => setEditing({ ...editing, text: e.target.value })}
              rows={4}
              placeholder="ॐ ..."
              className="devanagari mt-1"
              style={{ fontSize: '16px' }}
            />
          </div>

          <div>
            <Label className="devanagari-ui text-xs">अर्थ (वैकल्पिक)</Label>
            <Textarea
              value={editing.meaning || ''}
              onChange={(e) => setEditing({ ...editing, meaning: e.target.value })}
              rows={3}
              placeholder="मन्त्र का अर्थ..."
              className="devanagari-ui mt-1"
            />
          </div>

          {/* Audio upload */}
          <FileField
            label="ऑडियो फ़ाइल"
            icon={<Music className="w-4 h-4" />}
            value={editing.audioUrl}
            inputRef={audioInputRef}
            accept="audio/*"
            onSelect={(f) => handleFileUpload(f, 'audioUrl')}
            onClear={() => clearMedia('audioUrl')}
            onPickGallery={() => setGalleryFor('audioUrl')}
            color="green"
          />

          {/* Video upload */}
          <FileField
            label="वीडियो फ़ाइल"
            icon={<Video className="w-4 h-4" />}
            value={editing.videoUrl}
            inputRef={videoInputRef}
            accept="video/*"
            onSelect={(f) => handleFileUpload(f, 'videoUrl')}
            onClear={() => clearMedia('videoUrl')}
            onPickGallery={() => setGalleryFor('videoUrl')}
            color="blue"
          />

          {/* Meaning audio */}
          <FileField
            label="अर्थ ऑडियो (वैकल्पिक)"
            icon={<BookOpen className="w-4 h-4" />}
            value={editing.meaningAudioUrl}
            inputRef={meaningAudioInputRef}
            accept="audio/*"
            onSelect={(f) => handleFileUpload(f, 'meaningAudioUrl')}
            onClear={() => clearMedia('meaningAudioUrl')}
            onPickGallery={() => setGalleryFor('meaningAudioUrl')}
            color="purple"
          />

          {/* Pause after toggle */}
          <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div>
              <p className="text-sm font-medium text-stone-800 devanagari-ui">इस मन्त्र के बाद विराम लें</p>
              <p className="text-xs text-stone-500 devanagari-ui">अगला मन्त्र अपने आप नहीं चलेगा</p>
            </div>
            <Switch
              checked={!!editing.pauseAfter}
              onCheckedChange={(v) => setEditing({ ...editing, pauseAfter: v })}
            />
          </div>

          {/* Gayatri-style repeat */}
          <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div>
              <p className="text-sm font-medium text-stone-800 devanagari-ui">गायत्री-शैली बार-बार दोहराएं</p>
              <p className="text-xs text-stone-500 devanagari-ui">Settings में निर्धारित संख्या के अनुसार आहुति दोहराई जाएगी</p>
            </div>
            <Switch
              checked={!!editing.isGayatriAhuti}
              onCheckedChange={(v) => setEditing({ ...editing, isGayatriAhuti: v })}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="devanagari-ui">रद्द करें</Button>
          <Button onClick={onSave} className="bg-orange-600 hover:bg-orange-700 devanagari-ui">सहेजें</Button>
        </DialogFooter>

        {/* Media Gallery picker */}
        <MediaGallery
          open={!!galleryFor}
          onClose={() => setGalleryFor(null)}
          mediaType={galleryFor}
          onSelect={handleGalleryPick}
        />
      </DialogContent>
    </Dialog>
  );
}

function FileField({ label, icon, value, inputRef, accept, onSelect, onClear, onPickGallery, color }) {
  const colorMap = {
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
  };
  return (
    <div>
      <Label className="devanagari-ui text-xs flex items-center gap-1.5">{icon} {label}</Label>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onSelect(e.target.files?.[0])}
      />
      {value ? (
        <div className={`mt-1 flex items-center justify-between p-2.5 rounded-lg border ${colorMap[color]}`}>
          <span className="text-xs font-medium devanagari-ui">फ़ाइल चयनित</span>
          <button onClick={onClear} className="p-1 hover:bg-white/50 rounded" aria-label="Clear">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="mt-1 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex items-center justify-center gap-1.5 p-2.5 border-2 border-dashed border-stone-300 hover:border-orange-400 hover:bg-orange-50 rounded-lg text-stone-600 text-xs transition-colors devanagari-ui"
          >
            <Upload className="w-3.5 h-3.5" />
            अपलोड करें
          </button>
          <button
            type="button"
            onClick={onPickGallery}
            className="flex items-center justify-center gap-1.5 p-2.5 border-2 border-dashed border-orange-300 hover:border-orange-500 hover:bg-orange-50 rounded-lg text-orange-700 text-xs transition-colors devanagari-ui"
          >
            <Images className="w-3.5 h-3.5" />
            गैलरी से
          </button>
        </div>
      )}
      <p className="text-[10px] text-stone-400 mt-1 devanagari-ui">अधिकतम 50MB</p>
    </div>
  );
}
